import React, { useState } from 'react';
import { PlayCircle, CheckCircle2, Clock, RotateCcw, ChevronRight, AlertCircle } from 'lucide-react';

interface Task {
    id: string;
    title: string;
    description: string;
    duration: string;
    completed: boolean;
}

const DAILY_TASKS: Task[] = [
    { id: 'dev', title: 'BizBuilder App Dev', description: 'Execute critical implementation tasks from current plan', duration: '60 min', completed: false },
    { id: 'outreach', title: 'Daily 10 Outreach', description: 'Conduct structured prospect outreach', duration: '30 min', completed: false },
    { id: 'respond', title: 'Respond to Prospects', description: 'Process all Daily 10 Outreach responses', duration: '15 min', completed: false },
    { id: 'log', title: 'Log Activities', description: 'Update Focus View data sheets with completed activities', duration: '10 min', completed: false },
    { id: 'sales', title: 'Complete Priority Sales Tasks', description: 'Address remaining open CJMS activities and opportunities', duration: '20 min', completed: false },
    { id: 'crm', title: 'Update CRM', description: 'Synchronise all relevant CRM tabs and data sheets', duration: '10 min', completed: false },
    { id: 'lessons', title: 'Pull and Read Lessons', description: 'Access and process next BizBuilding Program drip content', duration: '15 min', completed: false },
    { id: 'tasks', title: 'Complete Action Tasks', description: 'Execute corresponding playbook tasks from Master_Tasks', duration: '20 min', completed: false },
    { id: 'sync', title: 'Sync and Log Day', description: 'Complete Focus View synchronisation and daily logging', duration: '10 min', completed: false },
];

const DailyRoutineExecution: React.FC = () => {
    const [tasks, setTasks] = useState<Task[]>(DAILY_TASKS);
    const [started, setStarted] = useState(false);

    const toggleTask = (id: string) => {
        setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
    };

    const resetDay = () => {
        setTasks(DAILY_TASKS.map(t => ({ ...t, completed: false })));
        setStarted(false);
    };

    const completed = tasks.filter(t => t.completed).length;
    const totalTime = tasks.reduce((s, t) => s + parseInt(t.duration), 0);
    const allDone = completed === tasks.length;

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-[2rem] border border-slate-100">
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase tracking-wider mb-2">
                        <CheckCircle2 size={14} /> Completed
                    </div>
                    <div className="text-3xl font-black text-slate-800">{completed}<span className="text-slate-300 text-xl">/{tasks.length}</span></div>
                </div>
                <div className="bg-white p-6 rounded-[2rem] border border-slate-100">
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase tracking-wider mb-2">
                        <Clock size={14} /> Total Time
                    </div>
                    <div className="text-3xl font-black text-slate-800">{totalTime}<span className="text-slate-300 text-xl text-sm ml-1">min</span></div>
                </div>
                <div className="bg-white p-6 rounded-[2rem] border border-slate-100 col-span-2">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Daily Progress</span>
                        <span className="text-xs font-bold text-blue-600">{Math.round(completed / tasks.length * 100)}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500" style={{ width: `${completed / tasks.length * 100}%` }} />
                    </div>
                </div>
            </div>

            {!started ? (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-[3rem] border border-slate-100 text-center space-y-4">
                    <div className="p-6 bg-blue-50 rounded-full text-blue-600">
                        <PlayCircle size={48} />
                    </div>
                    <h3 className="text-xl font-black text-slate-800">Start Your Daily Routine</h3>
                    <p className="text-slate-400 text-sm max-w-sm mx-auto">9 tasks · {totalTime} minutes total. Work through each one to complete your day.</p>
                    <button onClick={() => setStarted(true)}
                        className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20">
                        Begin Routine
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {tasks.map((task, i) => (
                        <div key={task.id}
                            onClick={() => toggleTask(task.id)}
                            className={`flex items-center gap-4 p-4 rounded-2xl border transition-all cursor-pointer ${
                                task.completed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-100 hover:border-blue-200'
                            }`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                                task.completed ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'
                            }`}>
                                {task.completed ? <CheckCircle2 size={16} /> : <span className="text-xs font-bold">{i + 1}</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className={`font-bold text-sm ${task.completed ? 'text-emerald-700 line-through' : 'text-slate-800'}`}>
                                    {task.title}
                                </h4>
                                <p className={`text-xs ${task.completed ? 'text-emerald-500' : 'text-slate-400'}`}>{task.description}</p>
                            </div>
                            <span className="text-xs font-bold text-slate-400 flex items-center gap-1">
                                <Clock size={12} /> {task.duration}
                            </span>
                        </div>
                    ))}

                    {allDone && (
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 rounded-[2rem] text-white text-center space-y-2 mt-4">
                            <CheckCircle2 size={32} className="mx-auto" />
                            <h3 className="text-xl font-black">Day Complete!</h3>
                            <p className="text-emerald-100 text-sm">All 9 tasks finished. Great work.</p>
                        </div>
                    )}

                    <div className="flex justify-center pt-4">
                        <button onClick={resetDay}
                            className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors text-xs font-black uppercase tracking-widest">
                            <RotateCcw size={14} /> Reset Day
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyRoutineExecution;
